// NetshineApp.jsx — Unified Netshine Hospitality Platform
// ONE app, ONE route: <Route path="/platform/*" element={<NetshineApp />} />
//
// Two sections handled inside the same app:
//   Customer Engagement  →  dashboard, journeys, campaigns, templates…
//   Forms & Surveys      →  manage forms, responses, review, reports, integration
//
// HOW THE NAV WORKS
//   Row 1: Brand | [Active section pill] [Inactive section pill] | Quick actions | Hotel ⚙
//   Row 2: Sub-tabs for the ACTIVE section only — auto-switches when section changes
//
// When user lands on engagement dashboard → Engagement active, Forms pill is clickable
// When user clicks Forms pill → switches to forms-manage, tabs change to Forms tabs
// No page reload, no separate route, no separate app.

import React, { useState, useCallback } from "react";

// ── Engagement ────────────────────────────────────────────────────────────────
import EngagementDashboard      from "./engagement/engagement-components/EngagementDashboard";
import JourneyList               from "./engagement/engagement-components/JourneyList";
import JourneyBuilder            from "./engagement/engagement-components/JourneyBuilder";
import CampaignList              from "./engagement/engagement-components/CampaignList";
import CampaignForm              from "./engagement/engagement-components/CampaignForm";
import TemplateManager           from "./engagement/engagement-components/TemplateManager";
import LandingPageManager        from "./engagement/engagement-components/LandingPageManager";
import GuestCRM                  from "./engagement/engagement-components/GuestCRM";
import ProfileCompletionBuilder  from "./engagement/engagement-components/ProfileCompletionBuilder";
import AnalyticsPage             from "./engagement/engagement-components/AnalyticsPage";
import ROIDashboard              from "./engagement/engagement-components/ROIDashboard";
import CreditsManager            from "./engagement/engagement-components/CreditsManager";
import Settings                  from "./engagement/engagement-components/Settings";
import AITools                   from "./engagement/engagement-components/AITools";
import { AI_ENABLED }            from "./engagement/engagement-components/shared";

// ── Forms & Surveys ───────────────────────────────────────────────────────────
import ManageSurveys         from "./feedback/FeedbackSetting";
import FeedbackResponses     from "./feedback/FeedbackResponses";
import FeedbackOverview      from "./feedback/reports/FeedbackOverview";
import ReviewDashboard       from "./feedback/reports/ReviewDashboard";
import ReportFeedbackLanding from "./feedback/reports/ReportFeedbackLanding";
import FeedbackIntegration   from "./feedback/integration";

// ─────────────────────────────────────────────────────────────────────────────
// SECTION CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const ENGAGEMENT_TABS = [
  { key: "dashboard",          label: "Dashboard",     icon: "fa-tachometer-alt" },
  { key: "journeys",           label: "Journeys",      icon: "fa-route"          },
  { key: "campaigns",          label: "Campaigns",     icon: "fa-paper-plane"    },
  { key: "templates",          label: "Templates",     icon: "fa-file-alt"       },
  { key: "landing-pages",      label: "Offer Pages",   icon: "fa-file-image"     },
  { key: "customers",          label: "Customers",     icon: "fa-users"          },
  { key: "profile-completion", label: "Profile Nudge", icon: "fa-user-edit"      },
  { key: "analytics",          label: "Analytics",     icon: "fa-chart-bar"      },
  { key: "roi",                label: "ROI",           icon: "fa-trophy"         },
  { key: "credits",            label: "Credits",       icon: "fa-coins"          },
  { key: "settings",           label: "Settings",      icon: "fa-cog"            },
  ...(AI_ENABLED ? [{ key: "ai-tools", label: "AI Tools", icon: "fa-robot" }] : []),
];

const FORMS_TABS = [
  { key: "forms-overview",    label: "Overview",     icon: "fa-chart-bar"  },
  { key: "forms-manage",      label: "Manage Forms", icon: "fa-wpforms"    },
  { key: "forms-responses",   label: "Responses",    icon: "fa-comments"   },
  { key: "forms-review",      label: "Review Dash",  icon: "fa-star"       },
  { key: "forms-reports",     label: "Reports",      icon: "fa-chart-line" },
  { key: "forms-integration", label: "Integration",  icon: "fa-plug"       },
];

const FORMS_PAGE_KEYS = new Set([
  "forms-overview","forms-manage","forms-responses",
  "forms-review","forms-reports","forms-integration",
]);

const sectionForPage = (p) => FORMS_PAGE_KEYS.has(p) ? "forms" : "engagement";

const activeTabKey = (p) => {
  if (p.startsWith("journeys"))  return "journeys";
  if (p.startsWith("campaigns")) return "campaigns";
  return p;
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE RENDERER
// ─────────────────────────────────────────────────────────────────────────────
const renderPage = (page, data, nav) => {
  switch (page) {
    case "dashboard":           return <EngagementDashboard onNavigate={nav} />;
    case "journeys":            return <JourneyList         onNavigate={nav} />;
    case "journeys-create":     return <JourneyBuilder      editingJourney={null} onNavigate={nav} />;
    case "journeys-edit":       return <JourneyBuilder      editingJourney={data} onNavigate={nav} />;
    case "campaigns":           return <CampaignList        onNavigate={nav} />;
    case "campaigns-create":    return <CampaignForm        editingCampaign={null} onNavigate={nav} />;
    case "campaigns-edit":      return <CampaignForm        editingCampaign={data} onNavigate={nav} />;
    case "templates":           return <TemplateManager     onNavigate={nav} />;
    case "landing-pages":       return <LandingPageManager  onNavigate={nav} />;
    case "customers":           return <GuestCRM            onNavigate={nav} />;
    case "profile-completion":  return <ProfileCompletionBuilder onNavigate={nav} />;
    case "analytics":           return <AnalyticsPage />;
    case "roi":                 return <ROIDashboard />;
    case "credits":             return <CreditsManager />;
    case "settings":            return <Settings />;
    case "ai-tools":            return <AITools onNavigate={nav} />;
    case "forms-overview":      return <FeedbackOverview />;
    case "forms-manage":        return <ManageSurveys />;
    case "forms-responses":     return <FeedbackResponses />;
    case "forms-review":        return <ReviewDashboard />;
    case "forms-reports":       return <ReportFeedbackLanding />;
    case "forms-integration":   return <FeedbackIntegration />;
    default:                    return <EngagementDashboard onNavigate={nav} />;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// TOP NAV
// ─────────────────────────────────────────────────────────────────────────────
const TopNav = ({ section, activePage, onNavigate }) => {
  const isEngagement = section === "engagement";
  const activeColor  = isEngagement ? "#0d6efd" : "#198754";
  const tabs         = isEngagement ? ENGAGEMENT_TABS : FORMS_TABS;

  // Quick actions differ per section
  const quickActions = isEngagement ? [
    { label: "+ Journey",   bg: "#eff6ff", color: "#1d4ed8", onClick: () => onNavigate("journeys-create")  },
    { label: "+ Campaign",  bg: "#f0fdf4", color: "#15803d", onClick: () => onNavigate("campaigns-create") },
    { label: "Templates",   bg: "#faf5ff", color: "#6d28d9", onClick: () => onNavigate("templates")        },
    { label: "Offer Pages", bg: "#fff7ed", color: "#c2410c", onClick: () => onNavigate("landing-pages")    },
  ] : [
    { label: "+ New Form",  bg: "#f0fdf4", color: "#15803d", onClick: () => onNavigate("forms-manage")     },
    { label: "Responses",   bg: "#eff6ff", color: "#1d4ed8", onClick: () => onNavigate("forms-responses")  },
  ];

  return (
    <div className="netshine-platform-nav bg-white"
      style={{ position: "sticky", top: 0, zIndex: 1040,
        boxShadow: "0 1px 0 #e5e7eb, 0 4px 16px rgba(0,0,0,0.06)" }}>

      {/* ── ROW 1 ─────────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center px-3"
        style={{ height: 54, gap: 8 }}>

        {/* Brand */}
        <button onClick={() => onNavigate("dashboard")} className="border-0 d-flex align-items-center gap-2"
          style={{ background: "transparent", cursor: "pointer", padding: "4px 8px 4px 4px",
            borderRadius: 10, flexShrink: 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10,
            background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(79,70,229,0.35)" }}>
            <i className="fas fa-hotel" style={{ color: "#fff", fontSize: "0.82rem" }} />
          </div>
          <span style={{ fontWeight: 900, fontSize: "1rem", color: "#111827",
            letterSpacing: "-0.04em" }}>
            Netshine
          </span>
        </button>

        <div style={{ width: 1, height: 24, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

        {/* Section pills */}
        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
          <button
            onClick={() => onNavigate("dashboard")}
            className="border-0 d-flex align-items-center gap-2"
            style={{
              background:   isEngagement ? "linear-gradient(135deg, #4f46e5, #6366f1)" : "#f5f5ff",
              color:        isEngagement ? "#fff" : "#4f46e5",
              fontWeight:   700,
              fontSize:     "0.81rem",
              padding:      "6px 15px",
              borderRadius: 22,
              cursor:       "pointer",
              border:       isEngagement ? "none" : "1.5px solid #c7d2fe",
              boxShadow:    isEngagement ? "0 2px 10px rgba(79,70,229,0.3)" : "none",
              transition:   "all 0.15s",
              flexShrink:   0,
            }}>
            <i className={`fas ${isEngagement ? "fa-bullhorn" : "fa-arrow-left"}`}
              style={{ fontSize: "0.72rem" }} />
            Engagement
          </button>

          <button
            onClick={() => onNavigate("forms-manage")}
            className="border-0 d-flex align-items-center gap-2"
            style={{
              background:   !isEngagement ? "linear-gradient(135deg, #059669, #10b981)" : "#f0fdf4",
              color:        !isEngagement ? "#fff" : "#059669",
              fontWeight:   700,
              fontSize:     "0.81rem",
              padding:      "6px 15px",
              borderRadius: 22,
              cursor:       "pointer",
              border:       !isEngagement ? "none" : "1.5px solid #a7f3d0",
              boxShadow:    !isEngagement ? "0 2px 10px rgba(5,150,105,0.3)" : "none",
              transition:   "all 0.15s",
              flexShrink:   0,
            }}>
            <i className="fas fa-wpforms" style={{ fontSize: "0.72rem" }} />
            Forms &amp; Surveys
          </button>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Hotel info + avatar */}
        <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
          <div style={{ textAlign: "right", lineHeight: 1.3 }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#111827" }}>
              {localStorage.getItem("companyname") || "Netshine Hospitality"}
            </div>
            <div style={{ fontSize: "0.64rem", color: "#9ca3af", fontWeight: 500 }}>Administrator</div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: "50%",
            background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.25)",
            flexShrink: 0 }}
            onClick={() => onNavigate("settings")}
            title="Settings">
            <i className="fas fa-user" style={{ color: "#fff", fontSize: "0.75rem" }} />
          </div>
        </div>
      </div>

      {/* ── ROW 2: Sub-tabs ────────────────────────────────────────────────── */}
      <div style={{ position: "relative", borderTop: "1px solid #f0f0f0",
        background: "#fafafa" }}>
        <div id="netshine-tabs" style={{
          overflowX: "auto", overflowY: "hidden",
          paddingLeft: 12, paddingRight: 44,
          scrollbarWidth: "none", msOverflowStyle: "none",
          display: "flex", alignItems: "stretch",
        }}>
          <nav className="d-flex" style={{ minWidth: "max-content", flex: 1 }}>

            {/* Back to Engagement when in Forms */}
            {!isEngagement && (
              <button
                onClick={() => onNavigate("dashboard")}
                className="border-0 d-flex align-items-center gap-1"
                style={{
                  background:   "transparent",
                  fontSize:     "0.8rem",
                  fontWeight:   600,
                  color:        "#4f46e5",
                  padding:      "10px 14px 10px 10px",
                  borderBottom: "2.5px solid transparent",
                  borderRight:  "1px solid #ebebeb",
                  cursor:       "pointer",
                  whiteSpace:   "nowrap",
                  marginRight:  4,
                }}>
                <i className="fas fa-arrow-left" style={{ fontSize: "0.7rem" }} />
                {" "}Engagement
              </button>
            )}

            {/* Current section tabs */}
            {tabs.map((tab) => {
              const active = activePage === tab.key;
              return (
                <button key={tab.key} onClick={() => onNavigate(tab.key)}
                  className="border-0 d-flex align-items-center gap-2"
                  style={{
                    background:   "transparent",
                    fontSize:     "0.8rem",
                    fontWeight:   active ? 800 : 500,
                    color:        active ? activeColor : "#4b5563",
                    padding:      "10px 15px",
                    borderBottom: active ? `2.5px solid ${activeColor}` : "2.5px solid transparent",
                    cursor:       "pointer",
                    whiteSpace:   "nowrap",
                    transition:   "color 0.12s",
                    letterSpacing: active ? "-0.01em" : "normal",
                  }}
                  aria-current={active ? "page" : undefined}>
                  <i className={`fas ${tab.icon}`}
                    style={{ fontSize: "0.72rem", opacity: active ? 1 : 0.6 }} />
                  {tab.label}
                </button>
              );
            })}

            {/* Forms pin when in Engagement */}
            {isEngagement && (
              <button
                onClick={() => onNavigate("forms-manage")}
                className="border-0 d-flex align-items-center gap-1"
                style={{
                  background:   "transparent",
                  fontSize:     "0.8rem",
                  fontWeight:   500,
                  color:        "#059669",
                  padding:      "10px 14px",
                  borderBottom: "2.5px solid transparent",
                  borderLeft:   "1px solid #ebebeb",
                  cursor:       "pointer",
                  whiteSpace:   "nowrap",
                  marginLeft:   4,
                }}>
                <i className="fas fa-wpforms" style={{ fontSize: "0.72rem" }} />
                {" "}Forms &amp; Surveys
              </button>
            )}
          </nav>
        </div>
        <div style={{
          position: "absolute", top: 0, right: 0, width: 44, height: "100%",
          background: "linear-gradient(to right, transparent, #fafafa 80%)",
          pointerEvents: "none",
        }} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY = "netshine_platform_page";

const NetshineApp = ({ initialPage = "dashboard" }) => {
  const [page, setPage] = useState(() => {
    // Restore last visited page from sessionStorage so remounts don't reset to dashboard.
    // initialPage prop (e.g. passed by a route) takes highest priority.
    if (initialPage !== "dashboard") return initialPage;
    try { return sessionStorage.getItem(STORAGE_KEY) || initialPage; } catch { return initialPage; }
  });
  const [pageData, setPageData] = useState(null);

  const navigate = useCallback((targetPage, data = null) => {
    setPageData(data);
    setPage(targetPage);
    try { sessionStorage.setItem(STORAGE_KEY, targetPage); } catch {}
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="netshine-platform-root" style={{ minHeight: "100vh", background: "#f8f9fb" }}>

      <TopNav
        section={sectionForPage(page)}
        activePage={activeTabKey(page)}
        onNavigate={navigate}
      />

      <div style={{ padding: (FORMS_PAGE_KEYS.has(page) || page === "dashboard") ? "0" : "24px", minHeight: "calc(100vh - 94px)" }}>
        {renderPage(page, pageData, navigate)}
      </div>

      <style>{`
        .netshine-platform-root .page-wrapper-full {
          min-height: unset !important; height: auto !important;
          overflow: visible !important; padding: 0 !important;
        }
        .netshine-platform-root .page-wrapper-full > .content.container-fluid {
          padding: 0 !important;
        }
        .netshine-platform-nav { position: sticky; top: 0; z-index: 1040; }
        #netshine-tabs::-webkit-scrollbar { display: none; }
        .card         { border-radius: 12px !important; }
        .card-header  { border-radius: 12px 12px 0 0 !important; }
        .netshine-platform-root button:hover { opacity: 0.88; }
        .modal-right .modal-dialog {
          position: fixed !important; top: 0 !important; right: 0 !important;
          margin: 0 !important; height: 100vh !important; max-height: 100vh !important;
          width: 60vw !important; max-width: 60vw !important;
          transform: none !important; display: flex !important; flex-direction: column !important;
        }
        .modal-right .modal-content {
          height: 100vh !important; max-height: 100vh !important;
          border-radius: 0 !important; display: flex; flex-direction: column;
          border: none; box-shadow: -8px 0 40px rgba(0,0,0,0.14);
          overflow: hidden !important;
        }
        .modal-right .modal-header { display: none !important; }
        .modal-right .modal-body { padding: 0 !important; overflow: hidden !important; flex: 1; display: flex; flex-direction: column; }
        .modal-right .modal-footer { display: none !important; }
        .ant-card { border-radius: 12px !important; border: 1px solid #e5e7eb !important; }
        .ant-card:hover { box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important; }
      `}</style>
    </div>
  );
};

export default NetshineApp;
